import { 
    createContext, useCallback, useContext, useEffect, useMemo, useState 
} from 'react'
import PropTypes from 'prop-types'
import { getUser, login, logout, ping, switchSesion, unSwitchSesion, updateUserSession } from '../utils/api/api.session'
import useLocalStorage from '../hooks/useLocalStorage'
import { _sessionDefaults } from '../utils/dataDefaults'
import { useAlerts } from '../hooks/useAlerts'
import { updateAjusteNotificacion } from '../utils/api/api.ajustes_notficaciones'
import { updateAjusteMetrica } from '../utils/api/api.ajustes_metricas'
import TransitionPage from '../pages/utils/TransitionPage'
import { useIsAppRoute } from '../hooks/useIsAppRoute'

const AuthContext = createContext()

const AuthProvider = ({ children }) => {

    const [isInitialized,setIsInitialized] = useState(false)
    const [exiting, setExiting] = useState(false)

    const { isAppPath } = useIsAppRoute()

    const [session,setSession] = useLocalStorage('session',_sessionDefaults)
    const { alertResponse } = useAlerts()

    const token = session.auth?.token
    const { user } = session

    const firstName = user?.name?.split(' ')[0] || ''

    const getUserCtx = useCallback(async() => {
        const ret = await getUser(token)
        const newSession = { ...session, user : ret.user  }
        setSession(newSession)
    },[session,setSession,token])

    const loginCtx = useCallback(async(email,pass) => {
        const ret = await login(email,pass)
        if(ret.code === 200){
            setSession({
                user : ret.user,
                auth : {
                    token : ret.access_token,
                    expiration : ret.expires_at
                }
            })
        }
        return ret
    },[setSession])

    const switchSesionCtx = useCallback(async(id) => {
        const ret = await switchSesion(token, id)
        if(ret.code === 200){
            setSession({
                user : ret.user,
                auth : {
                    token : ret.access_token,
                    expiration : ret.expires_at
                }
            })
        }
        return ret
    },[setSession, token])

    const unSwitchSesionCtx = useCallback(async(hash) => {
        const ret = await unSwitchSesion(token, hash)
        if(ret.code === 200){
            setSession({
                user : ret.user,
                auth : {
                    token : ret.access_token,
                    expiration : ret.expires_at
                }
            })
        }
        return ret
    },[setSession, token])

    const setSessionCtx = useCallback((ret) => {
        setSession({
            user : ret.user,
            auth : {
                token : ret.access_token,
                expiration : ret.expires_at
            }
        })
    },[setSession])

    const updateUserCtx = useCallback(async(name,celular,avatar) => {
        const ret = await updateUserSession(token,name,celular,avatar)
        alertResponse(ret)
        if(ret?.code === 200){
            const newSession = { ...session, user : ret.user  }
            setSession(newSession)
        }
    },[token, session, setSession, alertResponse])

    const updateAjusteNotificacionCtx = useCallback(async(id, val) => {
        const ret = await updateAjusteNotificacion(
            token, id, val
        )
        if(ret?.code === 200){
            const newSession = { ...session, user : ret.user  }
            setSession(newSession)
        }
        return ret
    },[token, session, setSession])

    const updateAjusteMetricaCtx = useCallback(async(id, val) => {
        const ret = await updateAjusteMetrica(
            token, id, val
        )
        if(ret?.code === 200){
            const newSession = { ...session, user : ret.user  }
            setSession(newSession)
        }
        return ret
    },[token, session, setSession])

    const logoutCtx = useCallback(async() => {
        setExiting(true)
        await logout(token)
        setTimeout(() => {
            setSession(_sessionDefaults)
            setExiting(false)
        }, 800)
    },[setSession,token])

    const init = useCallback(async() => {
        if(token && isAppPath){
            const ret = await ping(token)
            if(ret?.code === 401 || ret?.code === 403){
                await logoutCtx()
            }
        }
        setIsInitialized(true)
    },[logoutCtx, token, isAppPath])

    useEffect(() => {
        init()
        // eslint-disable-next-line
    },[])

    const memorizedValue = useMemo(
        () => ({
            user, token, isInitialized, firstName, 
            loginCtx, logoutCtx, setSessionCtx,
            updateUserCtx, getUserCtx, 
            updateAjusteNotificacionCtx, 
            updateAjusteMetricaCtx, 
            switchSesionCtx, 
            unSwitchSesionCtx
        }),
        [
            user, token, isInitialized, firstName, 
            loginCtx, logoutCtx, setSessionCtx,
            updateUserCtx, getUserCtx, 
            updateAjusteNotificacionCtx, 
            updateAjusteMetricaCtx, 
            switchSesionCtx, 
            unSwitchSesionCtx
        ]
    )

    return (
        <AuthContext.Provider  value={memorizedValue}>
            {exiting ? (
                <TransitionPage />
            ): (
               children
            )}
        </AuthContext.Provider>
    )
}
AuthProvider.propTypes = {
    children: PropTypes.node,
}

const useAuthContext = () => {
    const auth = useContext(AuthContext)
    return auth
}

export { AuthProvider, useAuthContext }